import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

import { teamCreateMembersDtoSchema } from '@ekhodealer/ekho-common';
import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import { Form, Stack, useFormContextTyped } from '@ekhodealer/ekho-common/rev';

import { useCreateTeamMembers } from '../api/useCreateTeamMembers';

/** Maximum number of emails allowed per invite batch */
const MAX_EMAILS = 10;

/**
 * Parse a comma-separated string of emails into a trimmed, deduplicated array.
 * Filters out empty strings that result from trailing commas or extra whitespace.
 */
function parseEmails(raw: string): string[] {
	const parsed = raw
		.split(',')
		.map((email) => email.trim().toLowerCase())
		.filter(Boolean);

	return [...new Set(parsed)];
}

/**
 * Zod schema for validating comma-separated email input.
 * Validates that:
 * - At least one email is provided
 * - Maximum of MAX_EMAILS emails
 * - Each email is valid according to teamCreateMembersDtoSchema
 */
const addMemberFormSchema = z
	.object({
		emailInput: z
			.string()
			.min(1, 'Enter at least one email address.')
			.refine(
				(val) => {
					const emails = parseEmails(val);
					return emails.length > 0;
				},
				{ message: 'Enter at least one email address.' },
			)
			.refine(
				(val) => {
					const emails = parseEmails(val);
					return emails.length <= MAX_EMAILS;
				},
				{ message: `You can invite up to ${MAX_EMAILS} members at a time.` },
			)
			.refine(
				(val) => {
					const emails = parseEmails(val);
					const emailSchema = teamCreateMembersDtoSchema.shape.emails.element;
					return emails.every((email) => emailSchema.safeParse(email).success);
				},
				(val) => {
					const emails = parseEmails(val);
					const emailSchema = teamCreateMembersDtoSchema.shape.emails.element;
					const invalidEmails = emails.filter(
						(email) => !emailSchema.safeParse(email).success,
					);
					return {
						message:
							invalidEmails.length === 1
								? `Invalid email: ${invalidEmails[0]}`
								: `Invalid emails: ${invalidEmails.join(', ')}`,
					};
				},
			),
	})
	.refine(
		(data) => {
			const emails = parseEmails(data.emailInput);
			// At least one valid email is required
			const emailSchema = teamCreateMembersDtoSchema.shape.emails.element;
			return emails.some((email) => emailSchema.safeParse(email).success);
		},
		{ message: 'Enter at least one valid email address.', path: ['emailInput'] },
	);

type AddMemberFormData = z.infer<typeof addMemberFormSchema>;

export interface AddMemberFormProps {
	/** Callback invoked when invites are sent successfully. Receives the number of emails sent. */
	onSuccess?: (emailCount: number) => void;
	/** Whether the form is disabled */
	isDisabled?: boolean;
	/** Callback invoked when validation state changes */
	onValidationChange?: (hasValidEmail: boolean, isLoading: boolean) => void;
}

/** Imperative handle exposed by AddMemberForm for external submit and state access. */
export interface AddMemberFormRef {
	/** Triggers form submission programmatically. */
	submit: () => Promise<void>;
	/** Whether at least one valid email is entered. */
	hasValidEmail: boolean;
	/** Whether the form is currently submitting. */
	isLoading: boolean;
}

/**
 * Internal component that uses form context to expose submit and validation state.
 */
const AddMemberFormContent = forwardRef<
	AddMemberFormRef,
	AddMemberFormProps & { createMembersMutation: ReturnType<typeof useCreateTeamMembers> }
>(({ isDisabled, onValidationChange, createMembersMutation }, ref) => {
	const form = useFormContextTyped<typeof addMemberFormSchema>();
	const { control } = form;
	const formElementRef = useRef<HTMLFormElement | null>(null);

	// Use useWatch for reactive updates
	const emailInput = useWatch({
		control,
		name: 'emailInput',
		defaultValue: '',
	});

	// Find the form element (FormContainer renders it)
	useEffect(() => {
		const formElement = document.querySelector<HTMLFormElement>(
			'form[data-rev-component="form-container"]',
		);
		if (formElement) {
			formElementRef.current = formElement;
		}
	}, []);

	// CXP-2555: Disable "Send invites" until at least one valid email is entered
	const hasValidEmail = useMemo((): boolean => {
		if (!emailInput || emailInput.trim() === '') return false;
		const emails = parseEmails(emailInput);
		if (emails.length === 0) return false;
		const emailSchema = teamCreateMembersDtoSchema.shape.emails.element;
		return emails.some((email) => emailSchema.safeParse(email).success);
	}, [emailInput]);

	// Expose submit function and validation state via ref
	useImperativeHandle(
		ref,
		() => ({
			submit: async (): Promise<void> => {
				// Trigger form submission by calling the form element's requestSubmit
				// This will trigger FormContainer's onSubmit handler
				if (formElementRef.current) {
					formElementRef.current.requestSubmit();
				} else {
					// Fallback: try to find the form element
					const formElement = document.querySelector<HTMLFormElement>(
						'form[data-rev-component="form-container"]',
					);
					if (formElement) {
						formElement.requestSubmit();
					}
				}
			},
			hasValidEmail,
			isLoading: createMembersMutation.isPending,
		}),
		[hasValidEmail, createMembersMutation.isPending],
	);

	// Notify parent of validation state changes (including on mount)
	useEffect(() => {
		onValidationChange?.(hasValidEmail, createMembersMutation.isPending);
	}, [hasValidEmail, createMembersMutation.isPending, onValidationChange]);

	return (
		<Stack itemsSpacing='8'>
			<Form.Field.TextArea
				schemaField='emailInput'
				labelText='Email addresses'
				descriptionText={`Separate multiple emails with commas (max ${MAX_EMAILS})`}
				required
				isDisabled={isDisabled ?? createMembersMutation.isPending}
				placeholder='Enter email addresses separated by commas'
				rows={4}
			/>
		</Stack>
	);
});

AddMemberFormContent.displayName = 'AddMemberFormContent';

/**
 * AddMemberForm — Reusable form component for inviting team members via comma-separated email input.
 *
 * Uses the Form system (FormContainer + FormItem) for validation and state management.
 * Validates emails client-side using teamCreateMembersDtoSchema before submitting
 * through the useCreateTeamMembers mutation.
 */
export const AddMemberForm = forwardRef<AddMemberFormRef, AddMemberFormProps>(
	({ onSuccess, isDisabled = false, onValidationChange }, ref) => {
		const { user } = useAuth();
		const createMembersMutation = useCreateTeamMembers();

		const handleFormSubmit = useCallback(
			async (data: AddMemberFormData): Promise<{ isSuccess: boolean }> => {
				const emails = parseEmails(data.emailInput);
				const invitedBy = user?.email ?? user?.uid ?? '';

				try {
					await createMembersMutation.mutateAsync({
						emails,
						invitedBy,
					});

					const emailCount = emails.length;
					onSuccess?.(emailCount);
					// Return success but don't show default toast (we handle it in onSuccess)
					return { isSuccess: true };
				} catch {
					// The useCreateTeamMembers hook already shows an error toast via onError,
					// so we keep the form open for the user to retry or adjust.
					return { isSuccess: false };
				}
			},
			[user?.email, user?.uid, createMembersMutation, onSuccess],
		);

		return (
			<Form
				schema={addMemberFormSchema}
				defaultValues={{ emailInput: '' }}
				onSubmit={handleFormSubmit}
				listenForServerChanges={false}
				formOptions={{
					mode: 'onSubmit', // Only show validation errors after submit attempt
					reValidateMode: 'onChange', // Re-validate on change after first submit
				}}>
				<AddMemberFormContent
					ref={ref}
					isDisabled={isDisabled}
					onValidationChange={onValidationChange}
					createMembersMutation={createMembersMutation}
				/>
			</Form>
		);
	},
);

AddMemberForm.displayName = 'AddMemberForm';
